import {
  globalMessageHandler,
  GridModalFormProps,
  useDialog,
  useForm,
  Validator,
} from "common";
import { FieldType } from "components/FormEditor/types";
import I18n from "i18n-js";
import { useContext, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { WaiverListContext } from "./useWaiverList";
import {
  getWaiverService,
  updateWaiverStatus,
  WaiverFormItem,
  WaiverListItem,
  WaiverOptions,
  WaiverStatus,
  WaiverType,
} from "./waiverApi";

export interface UseWaiverFormProps extends GridModalFormProps {}

export default function useWaiverForm({ id, afterSave }: UseWaiverFormProps) {
  const params = useParams<"waiver_id">();
  const location = useLocation();

  const type: WaiverType | undefined = new URLSearchParams(location.search).get(
    "type"
  ) as WaiverType;

  const [error, setError] = useState<any>();
  const saveAndPublishRef = useRef<any>(undefined);

  const service = getWaiverService();
  const [previewOpen, setPreviewOpen, previewDialog] = useDialog();
  const [sharingOpen, setSharingOpen, sharingDialog] = useDialog();
  const waiverList = useContext(WaiverListContext);
  const form = useForm<WaiverFormItem, WaiverOptions>({
    id: params.waiver_id,
    //ignoreNavigateAfterCreate: true,
    translationCategory: "Waiver",
    defaultValues: {
      //name: `My Waiver ${waiverList?.grid?.rowCount}`,
      type,
      status: "ACTIVE",
    },
    rules: {
      name: Validator.rule.any().required(),
      //status: Validator.rule.any().required(),
      //contents: Validator.rule.any().required(),
      //fileIds: Validator.rule.any().required(),
    },
    afterSave(newItem, isCreate) {
      setError(undefined);
      if (afterSave) {
        afterSave(newItem, isCreate);
      }
      if (saveAndPublishRef.current) {
        saveAndPublishRef.current = undefined;
        setSharingOpen(true);
      }
    },
    afterLoad: _newItem => {},
    onValidationFailed: () => {
      globalMessageHandler.error(I18n.t("App.formHasErrors"));
    },
    customValidation: validateFields,
    ...service,
  });

  function validateSignature(item: WaiverListItem) {
    for (let blockIndex = 0; blockIndex < item.fields?.length; blockIndex++) {
      const block = item.fields[blockIndex];
      for (let rowIndex = 0; rowIndex < block.rows.length; rowIndex++) {
        const row = block.rows[rowIndex];
        for (let fieldIndex = 0; fieldIndex < row.fields.length; fieldIndex++) {
          const field = row.fields[fieldIndex];
          if (field.type === FieldType.signature) {
            return true;
          }
        }
      }
    }
  }
  function validateFields(item: WaiverListItem) {
    if (!item.fields || item.fields.length === 0) {
      setError("At least one field is required.");
      return { fields: "" };
    }

    return {};
  }

  function save() {
    if (!validateSignature(form.item)) {
      globalMessageHandler.confirm(
        I18n.t("Submit.signatureWarning"),
        () => form.save(),
        () => {}
      );
    } else {
      form.save();
    }
  }

  function saveAndPublish() {
    saveAndPublishRef.current = true;
    save();
  }

  const handleStatusChange = async (
    status: WaiverStatus,
    confirm: boolean = false
  ) => {
    const response = await updateWaiverStatus(
      params.waiver_id as any,
      status,
      confirm
    );

    if (response.success) {
      form.load();
    } else if (!confirm && response.error?.form_errors?.confirm?.[0]) {
      globalMessageHandler.confirm(
        response.error.form_errors.confirm[0],
        async () => {
          await handleStatusChange(status, true);
        }
      );
    }
  };

  /*const defaultSharingValues = useMemo(() => {
    //
    const defaultValues = { waiver_ids: [params.waiver_id as any] }
  }, [form.item, params.waiver_id]);*/

  return {
    ...form,
    save,
    error,
    setError,
    service,
    previewOpen,
    setPreviewOpen,
    previewDialog,
    saveAndPublish,
    sharingOpen,
    setSharingOpen,
    sharingDialog,
    params,
    handleStatusChange,
  };
}
